import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useBoxContext } from 'components/common/box';
import { getSelectedUsers } from 'lib/users';
import { ContainerWithShowAll, User } from 'components/common';

import type { UserWithBasicData } from 'shared-types';
import type { UserSelectionParams } from 'lib/users/getSelectedUsers';

export type SelectedUser = {
  identifier: string;
  id: number;
  user: UserWithBasicData;
};

interface Props {
  allowAnonymousUsers?: boolean;
  allowGuestUsers?: boolean;
  allowSelf?: boolean;
  includeAdmins?: boolean;
  onLoadedUsers: (users: SelectedUser[]) => void;
  supervisorRouteSupervisorId?: number;
  title?: string;
  userSelectionParams?: Partial<UserSelectionParams>;
}

const UsersListSelectedUsers = ({
  allowAnonymousUsers = false,
  allowGuestUsers = false,
  allowSelf = false,
  includeAdmins = true,
  onLoadedUsers,
  supervisorRouteSupervisorId,
  title,
  userSelectionParams
}: Props) => {
  const { selectedItems } = useBoxContext();

  const { dispatch } = useDispatch();

  const router = useRouter();

  const {
    messages,
    pageState: { courseAccess, search },
    serverConfig: { accountEnableUsernameAccounts },
    user: currentUser
  } = useReduxState();

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<SelectedUser[]>([]);

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    onLoadedUsers(users);
  }, [users]);

  const getUsers = async () => {
    setLoading(true);

    const data = await getSelectedUsers({
      allowAnonymousUsers,
      allowGuestUsers,
      courseAccess,
      search,
      selectedUserIds: Object.keys(selectedItems).map((id) => parseInt(id, 10)),
      routerQueryParams: router.query,
      supervisorRouteSupervisorId,
      userSelector:
        accountEnableUsernameAccounts || allowGuestUsers
          ? 'usernames'
          : 'emails',
      ...(userSelectionParams || {})
    });

    if (data.error) await dispatch.handleErrorDispatch(null);
    else {
      setUsers(
        data.identifiers
          .map((identifier: string, i) => ({
            identifier,
            id: data.userIds[identifier],
            user: data.users[i]
          }))
          .filter((user: SelectedUser) =>
            allowSelf
              ? user.id !== 1 &&
                (includeAdmins || !user.user.roles.includes('admin'))
              : user.id !== currentUser.id &&
                user.id !== 1 &&
                (includeAdmins || !user.user.roles.includes('admin'))
          )
      );
    }

    setLoading(false);
  };

  return (
    <ContainerWithShowAll
      items={users?.map((user, i) => (
        <div key={`${user.identifier}-${i}`} style={{ width: 'max-content' }}>
          <User user={user.user} />
        </div>
      ))}
      loading={loading}
      title={
        title
          ? title
          : getMessageWithPlaceholders({
              message: messages.usersNumberOfAccounts,
              placeholders: {
                numberOfAccounts: users.length
              }
            })
      }
    />
  );
};

export default UsersListSelectedUsers;
